/**
 * Handoff to M2 + re-map diffing. `assembleGeocodeRequest` is the ONLY thing
 * this layer emits across the M2 boundary (criterion 9): a structured intent
 * payload — mode, per-row {row_key, query|lat/lng}, country default. No
 * geocoder or tile call happens here. `diffGeocodeRequests` powers re-map reuse
 * (criterion 8): only changed row_keys need re-geocoding; unchanged rows reuse
 * M2's cache.
 */

import {
  CURRENT_GEOCODE_SCHEMA_VERSION,
  type GeocodeBatchOpts,
  type GeocodeBatchRequest,
  type GeocodeRowInput,
} from "@/lib/contracts/geocode";
import type { ColumnMapping, ParsedRow } from "./types";
import { buildAddressQuery, parseCoords } from "./rows";

const cellAt = (row: string[], idx: number | undefined): string =>
  idx === undefined ? "" : (row[idx] ?? "").trim();

export interface AssembleParams {
  dataset_id: string;
  mapping: ColumnMapping;
  rows: ParsedRow[];
  country_default: string;
  /** row_keys the user excluded via the attention tray. */
  excluded?: Iterable<string>;
  opts?: GeocodeBatchOpts;
}

/** Build the GeocodeRequest DTO from ready, non-excluded rows. */
export function assembleGeocodeRequest(params: AssembleParams): GeocodeBatchRequest {
  const excluded = new Set(params.excluded ?? []);
  const { mapping } = params;

  const rows: GeocodeRowInput[] = [];
  for (const row of params.rows) {
    if (row.status !== "ready" || row.row_key === "" || excluded.has(row.row_key)) continue;
    if (mapping.mode === "coordinate") {
      const res = parseCoords(cellAt(row.cells, mapping.roles.lat), cellAt(row.cells, mapping.roles.lng));
      if (res.ok) rows.push({ row_key: row.row_key, lat: res.lat, lng: res.lng });
    } else {
      rows.push({ row_key: row.row_key, query: buildAddressQuery(mapping, row.cells) });
    }
  }

  return {
    schema_version: CURRENT_GEOCODE_SCHEMA_VERSION,
    dataset_id: params.dataset_id,
    mode: mapping.mode,
    rows,
    country_default: params.country_default,
    ...(params.opts ? { opts: params.opts } : {}),
  };
}

export interface RequestDiff {
  /** Rows whose row_key is new/changed since the previous request — send to M2. */
  changed: GeocodeRowInput[];
  /** row_keys unchanged since the previous request — reuse cached coords. */
  unchanged: string[];
}

/**
 * Diff a new request against the previous one by row_key. Because row_key is a
 * hash of the normalized query/coords, any mapping change that alters a row's
 * query yields a new key; unchanged rows keep their key and are reused.
 */
export function diffGeocodeRequests(
  prev: GeocodeBatchRequest,
  next: GeocodeBatchRequest,
): RequestDiff {
  const prevKeys = new Set(prev.rows.map((r) => r.row_key));
  const changed: GeocodeRowInput[] = [];
  const unchanged: string[] = [];
  for (const r of next.rows) {
    if (prevKeys.has(r.row_key)) unchanged.push(r.row_key);
    else changed.push(r);
  }
  return { changed, unchanged };
}
